import { IContractCruMetric } from "dto/Contracts/IContractCru";
import { axiosInstance } from "./axios";
import { INewTag } from "./casesService";
import { ITemplate } from "dto/ITemplate";
import { ItemKind } from "dto/IKindItems";
import { IError } from "dto/IError";
import { IPackage } from "dto/IPackage";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";
import { IGetItemsResponse } from "dto/Dossier/IGetItemsResponse";

const queryKeys = {
  getTemplates: (name: string) => ["editorService.getTemplates", name],
  getSingleTemplate: (id: string, kind: string) => [
    "editorService.getSingleTemplate",
    id,
    kind,
  ],
  getVersions: (id: string) => ["editorService.getVersions", id],
};

export interface IEditDocument {
  Id: string;
  Title: string;
}

const editDocument = async (
  payload: IEditDocument
): Promise<{ data: IError }> => {
  return await axiosInstance.put("/editor/document", payload);
};

const useEditDocument = () => {
  return useCustomMutation(editDocument);
};
/** Rodzaj dokumentu edytora (BE: Data.Editor.DocumentType). */
export enum EditorDocumentType {
  None = 0,
  LegalAct = 1,
  /** Umowa — pismo z metryką CRU (wpis w rejestrze umów zakładany przy tworzeniu). */
  Contract = 2,
}

export interface ICreateDocument {
  CaseId: string | null;
  NewTags: INewTag[];
  PrivateKey: string;
  PublicKey: string;
  TagIds: string[];
  TemplateId: string | null;
  Title: string;
  Format: string;
  FolderId?: string;
  Type?: EditorDocumentType;
  /** Metryka CRU dla pisma rodzaju „Umowa" (walidacja odroczona). */
  ContractCruMetric?: IContractCruMetric | null;
}

const createDocument = async (
  payload: ICreateDocument
): Promise<{ data: { Items: IPackage[]; Id: string; KeyId: string } }> => {
  return await axiosInstance.post("editor/document", payload);
};

const useCreateDocument = () => {
  return useCustomMutation(createDocument);
};

const getTemplates = async (
  name: string
): Promise<{ data: IGetItemsResponse }> => {
  const query = new URLSearchParams(window.location.search);

  if (name.length) {
    query.append("Text", name);
  }

  return axiosInstance.get(`editor/templates?${query}`);
};

const useGetTemplate = (
  name: string,
  allowEmpty?: boolean,
  enabled: boolean = true,
  customName?: string
) => {
  return useCustomQuery(
    customName ?? queryKeys.getTemplates(name),
    () => getTemplates(name, allowEmpty),
    {
      enabled: enabled,
    }
  );
};

export interface ISign {
  Id: string;
  Uri: string;
  Report?: string;
  Countersign?: boolean;
}

const sign = (payload: ISign) => {
  return axiosInstance.post("/editor/sign", payload);
};

const useSign = () => {
  return useCustomMutation(sign);
};

const getSingleTemplate = async (
  id: string,
  kind: string
): Promise<ITemplate[]> => {
  const query = new URLSearchParams(window.location.search);

  query.append("Id", id);
  query.append("Kind", kind.toString());

  const r = await axiosInstance.get(`editor/templates?${query}`);
  return r.data ?? [];
};

const useGetSingleTemplate = (id: string, kind: string) => {
  return useCustomQuery(queryKeys.getSingleTemplate(id, kind), () =>
    getSingleTemplate(id, kind)
  );
};

const getVersions = async (id: string, kind: ItemKind) => {
  let url = "/editor/versions";

  if (kind === ItemKind.CaseFile) {
    url = "/dossier/file/versions";
  }
  const res = await axiosInstance.get(`${url}?Id=${id}`);
  return res.data ?? [];
};

const useGetVersions = (id: string, kind: ItemKind, enabled: boolean) => {
  return useCustomQuery(
    queryKeys.getVersions(id),
    () => getVersions(id, kind),
    {
      enabled: enabled,
    }
  );
};

export interface IForceVersion {
  Id: string;
  Name: string;
}

const forceVersion = (payload: IForceVersion) => {
  return axiosInstance.post("/editor/version", payload);
};

const useForceVersion = () => {
  return useCustomMutation(forceVersion);
};

export interface IUnlockEditor {
  Id: string;
  Name: string;
}

const unlockDocument = (payload: IUnlockEditor) => {
  return axiosInstance.post("/editor/unlock", payload);
};

const useUnlockDocument = () => {
  return useCustomMutation(unlockDocument);
};

export default {
  useCreateDocument,
  useGetTemplate,
  useSign,
  useEditDocument,
  useGetSingleTemplate,
  useGetVersions,
  useForceVersion,
  useUnlockDocument,
};
